import { useCallback, useRef, useState } from 'react';
import Map, { NavigationControl, Source, Layer, Popup } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@/styles/map.css';

import { authenticatedMapRequest, INITIAL_VIEW, MAP_STYLE, territoryTileUrl } from '@/config';
import { useMapStore } from '@/stores/mapStore';
import { useLayerStore } from '@/stores/layerStore';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useMapRecenter } from '@/hooks/useMapRecenter';
import { Legend } from './Legend';
import { PopupInfo } from './PopupInfo';
import { DataAttribution } from './DataAttribution';

interface ClickedFeature {
  longitude: number;
  latitude: number;
  properties: Record<string, unknown>;
  layer: string;
}

export function MapView2D() {
  const { setViewState } = useMapStore();
  const layers = useLayerStore((s) => s.layers);
  const territoryCode = useTerritoryStore((s) => s.activeTerritory?.code_insee);
  const mapRef = useRef<MapRef>(null);
  const [mapReady, setMapReady] = useState(false);
  const [clicked, setClicked] = useState<ClickedFeature | null>(null);

  // Recenter the map on the active territory's real extent (no fixed coords),
  // so overlays for a territory outside the initial bbox stay in view.
  useMapRecenter(mapRef, mapReady);

  const isVisible = (id: string) => layers.find((l) => l.id === id)?.visible ?? false;
  const getOpacity = (id: string) => layers.find((l) => l.id === id)?.opacity ?? 1;

  const handleClick = useCallback((e: MapLayerMouseEvent) => {
    const feature = e.features?.[0];
    if (feature) {
      setClicked({
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
        properties: feature.properties as Record<string, unknown>,
        layer: feature.layer.id,
      });
    } else {
      setClicked(null);
    }
  }, []);

  const handleMove = useCallback(
    (evt: { viewState: { longitude: number; latitude: number; zoom: number } }) => {
      setViewState(evt.viewState.longitude, evt.viewState.latitude, evt.viewState.zoom);
    },
    [setViewState],
  );

  return (
    <div className="map-container">
      <Map
        ref={mapRef}
        initialViewState={INITIAL_VIEW}
        style={{ width: '100%', height: '100%' }}
        mapStyle={MAP_STYLE}
        transformRequest={authenticatedMapRequest}
        onLoad={() => setMapReady(true)}
        onClick={handleClick}
        onMove={handleMove}
        interactiveLayerIds={['buildings-fill', 'roads-line', 'facilities-point', 'parcels-line']}
      >
        <NavigationControl position="top-right" />
        <DataAttribution />

        {/* Couche Territoires */}
        {territoryCode && isVisible('territories') && (
          <Source id="territories" type="vector" tiles={[territoryTileUrl(territoryCode, 'territories')]} minzoom={6} maxzoom={14}>
            <Layer
              id="territories-outline"
              type="line"
              source-layer="territories"
              paint={{
                'line-color': '#2563eb',
                'line-width': 2,
                'line-opacity': getOpacity('territories'),
              }}
            />
          </Source>
        )}

        {/* Couche Bâtiments */}
        {territoryCode && isVisible('buildings') && (
          <Source id="buildings" type="vector" tiles={[territoryTileUrl(territoryCode, 'buildings')]} minzoom={12} maxzoom={18}>
            <Layer
              id="buildings-fill"
              type="fill"
              source-layer="buildings"
              paint={{
                'fill-color': [
                  'match', ['get', 'dpe_label'],
                  'A', '#009B4D', 'B', '#33CC33', 'C', '#CCE500',
                  'D', '#FFCC00', 'E', '#FF9933', 'F', '#FF3300',
                  'G', '#CC0000',
                  '#AAAAAA',
                ] as never,
                'fill-opacity': getOpacity('buildings') * 0.85,
                'fill-outline-color': 'rgba(0,0,0,0.15)',
              }}
            />
          </Source>
        )}

        {/* Couche Voirie */}
        {territoryCode && isVisible('roads') && (
          <Source id="roads" type="vector" tiles={[territoryTileUrl(territoryCode, 'roads')]} minzoom={10} maxzoom={18}>
            <Layer
              id="roads-line"
              type="line"
              source-layer="roads"
              paint={{
                'line-color': '#666',
                'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 3],
                'line-opacity': getOpacity('roads'),
              }}
            />
          </Source>
        )}

        {/* Couche Végétation */}
        {territoryCode && isVisible('vegetation') && (
          <Source id="vegetation" type="vector" tiles={[territoryTileUrl(territoryCode, 'vegetation')]} minzoom={12} maxzoom={18}>
            <Layer
              id="vegetation-fill"
              type="fill"
              source-layer="vegetation"
              paint={{
                'fill-color': '#22c55e',
                'fill-opacity': getOpacity('vegetation') * 0.5,
              }}
            />
          </Source>
        )}

        {/* Couche Eau */}
        {territoryCode && isVisible('water') && (
          <Source id="water" type="vector" tiles={[territoryTileUrl(territoryCode, 'water')]} minzoom={10} maxzoom={18}>
            <Layer
              id="water-fill"
              type="fill"
              source-layer="water"
              paint={{
                'fill-color': '#3b82f6',
                'fill-opacity': getOpacity('water') * 0.5,
              }}
            />
          </Source>
        )}

        {/* Couche Équipements publics */}
        {territoryCode && isVisible('public_facilities') && (
          <Source id="facilities" type="vector" tiles={[territoryTileUrl(territoryCode, 'public_facilities')]} minzoom={12} maxzoom={18}>
            <Layer
              id="facilities-point"
              type="circle"
              source-layer="public_facilities"
              paint={{
                'circle-radius': 6,
                'circle-color': '#e11d48',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
                'circle-opacity': getOpacity('public_facilities'),
              }}
            />
          </Source>
        )}

        {/* Couche Parcelles */}
        {territoryCode && isVisible('parcels') && (
          <Source id="parcels" type="vector" tiles={[territoryTileUrl(territoryCode, 'parcels')]} minzoom={14} maxzoom={18}>
            <Layer
              id="parcels-line"
              type="line"
              source-layer="parcels"
              paint={{
                'line-color': '#a855f7',
                'line-width': 1,
                'line-opacity': getOpacity('parcels'),
              }}
            />
          </Source>
        )}

        {/* Popup info */}
        {clicked && (
          <Popup
            longitude={clicked.longitude}
            latitude={clicked.latitude}
            onClose={() => setClicked(null)}
            closeOnClick={false}
          >
            <PopupInfo properties={clicked.properties} layer={clicked.layer} />
          </Popup>
        )}
      </Map>

      {/* Légende */}
      <div className="map-overlay map-overlay-bottom-left">
        <Legend />
      </div>
    </div>
  );
}
